// ticket-v1-runnability.ts
//
// H14 (2026-09-29): which tickets can actually be dispatched in v1. The
// registry has `deploy-v1`/`revert-v1` rows (controller executors, empty
// `allowedTools`) but v1 has NO dispatch path for them — a ticket of that
// shape died three times at spawn ("allowedTools rỗng" → task_failed →
// blocked), and a deploy one first made a human approve G2 for work that
// could never run. The ticket schema keeps `kind`, `executor` and
// `command_adapter` as independent fields, so a proposal could also slip a
// controller adapter under an ordinary kind (amend `command_adapter` only),
// past the Bug L guard that watches `kind`.
//
// ONE predicate, used where a ticket ENTERS the mission: `applyProposal`
// (the ticket as it will be after the change — create, amend, redirect) and
// the `run` preflight (hand-written files, missions from an older CLI).
import { resolveCommandAdapter } from "./command-adapter.js";
import {
  BOOTSTRAP_KINDS,
  type ProposalRejectionCode,
  quoteWorkerValue,
} from "./proposal-change-types.js";
import type { Ticket } from "./schemas/ticket-schema.js";

/** Same words the `autopilot revert` command prints — one wording for CLI, directive and ticket guard. */
export const REVERT_NOT_SUPPORTED_V1_MESSAGE =
  'revert chưa hỗ trợ ở v1 — dùng redirect --text "<mô tả>"';

export const DEPLOY_VIA_RELEASE_FLOW_MESSAGE =
  "deploy chạy qua release flow (config.release + gate G3), không phải ticket";

/** Reason the ticket cannot be dispatched in v1, or `undefined` when it can. */
export function ticketUnrunnableInV1Reason(
  ticket: Pick<Ticket, "id" | "kind" | "executor" | "command_adapter">,
): string | undefined {
  if (ticket.kind === "deploy") return `ticket ${ticket.id}: ${DEPLOY_VIA_RELEASE_FLOW_MESSAGE}`;
  if (ticket.kind === "revert") return `ticket ${ticket.id}: ${REVERT_NOT_SUPPORTED_V1_MESSAGE}`;

  let adapter: ReturnType<typeof resolveCommandAdapter>;
  try {
    adapter = resolveCommandAdapter(ticket.command_adapter);
  } catch {
    return `ticket ${ticket.id}: command_adapter ${quoteWorkerValue(ticket.command_adapter)} không có trong bảng adapter`;
  }
  if (adapter.executor === "controller" || ticket.executor === "controller") {
    return (
      `ticket ${ticket.id}: executor "controller" (adapter ${adapter.id}) không có đường ` +
      "dispatch ở v1 — deploy đi qua release flow, revert chưa hỗ trợ"
    );
  }
  if (adapter.kind !== ticket.kind) {
    return (
      `ticket ${ticket.id}: command_adapter ${adapter.id} là kind "${adapter.kind}" ` +
      `nhưng ticket khai kind "${ticket.kind}" — hai trường phải khớp`
    );
  }
  return undefined;
}

/**
 * 3.38.0-beta.2 (F-72): refusal code for `ticketUnrunnableInV1Reason`, called
 * only once that refused. Uncoded (human-only): kind deploy/revert, a
 * controller ADAPTER under another kind (the H14 slip past Bug L) and a
 * survey/plan adapter under a feature kind. Coded
 * (fixable output): `executor: "controller"` on a worker adapter (dogfood m2)
 * and an unknown or kind-mismatched adapter.
 */
export function ticketUnrunnableInV1Code(
  ticket: Pick<Ticket, "kind" | "executor" | "command_adapter">,
): ProposalRejectionCode | undefined {
  if (ticket.kind === "deploy" || ticket.kind === "revert") return undefined;
  let adapter: ReturnType<typeof resolveCommandAdapter>;
  try {
    adapter = resolveCommandAdapter(ticket.command_adapter);
  } catch {
    return "adapter_mismatch";
  }
  // Audit F-5: a bootstrap (survey/plan) adapter under a feature kind dodges G0/G1.
  if (adapter.executor === "controller" || BOOTSTRAP_KINDS.includes(adapter.kind)) return undefined;
  // Audit Q2: `executor: "controller"` is fixable ONLY because v1 has no
  // dispatch path for it (pinned by a test over the adapter registry). A v2
  // controller dispatch path makes this a privilege escalation: re-decide.
  return ticket.executor === "controller" ? "executor" : "adapter_mismatch";
}

/**
 * Review L3: the way out the `run` preflight prints — only a controller-shaped
 * ticket (deploy/revert/controller executor) needs turning into a decision;
 * a plain kind/adapter mismatch just needs the two fields to agree. The
 * preflight reads ticket FILES, not journal status (D17), so a cancelled
 * deploy ticket still has to be rewritten or the mission aborted.
 */
export function unrunnableTicketWayOut(
  ticket: Pick<Ticket, "kind" | "executor" | "command_adapter">,
): string {
  let controllerAdapter = false;
  try {
    controllerAdapter = resolveCommandAdapter(ticket.command_adapter).executor === "controller";
  } catch {
    controllerAdapter = false;
  }
  const controllerShaped =
    ticket.kind === "deploy" ||
    ticket.kind === "revert" ||
    ticket.executor === "controller" ||
    controllerAdapter;
  const fix = controllerShaped
    ? 'sửa file thành kind "decision" + command_adapter "decision-v1" + executor "claude" rồi commit'
    : "đổi kind hoặc command_adapter cho khớp nhau (adapter nào khai kind đó) rồi commit";
  return `Cách thoát: ${fix}, hoặc \`avatar autopilot abort\` (preflight đọc file ticket, kể cả ticket đã cancelled).`;
}
